import { t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { format, parseISO } from "date-fns";
import { useEffect, useRef, useState } from "react";

import {
  nextOccurrenceDateAfter,
  cadenceOptions,
  cadenceValueForRecurrence,
  getRecurrenceForCadence,
  getAvailableRecurrenceCadenceValues,
  recurrenceEndDate,
  remainingOccurrenceCount,
} from "@/features/RecurringRequests/recurrence";
import { RecurrenceFormValue } from "@/features/RecurringRequests/types";
import { Button } from "@whoosh/design-system/src/components/controls";
import { DatePickerInput } from "@whoosh/design-system/src/components/controls/Datepickers";
import { DropdownSelect } from "@whoosh/design-system/src/components/controls/DropdownSelect";
import {
  Radio,
  RadioGroup,
} from "@whoosh/design-system/src/components/controls/RadioList";
import { StandardModal } from "@whoosh/design-system/src/components/overlay/StandardModal";
import {
  HStack,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import { defaultRecurrenceValue } from "../../formatters";

type RecurringRequestModalProps = {
  allowSingleOccurrence?: boolean;
  clubWeekStartsOn: 0 | 1;
  initialValue?: RecurrenceFormValue;
  isOpen: boolean;
  lockCadence?: boolean;
  maximumEndDate: string;
  onClose: () => void;
  onSavePending: (value: RecurrenceFormValue) => void;
  sourceDate: string;
};

export const RecurringRequestModal = ({
  allowSingleOccurrence = false,
  clubWeekStartsOn,
  initialValue,
  isOpen,
  lockCadence = false,
  maximumEndDate,
  onClose,
  onSavePending,
  sourceDate,
}: RecurringRequestModalProps) => {
  const { i18n } = useLingui();
  const [value, setValue] = useState<RecurrenceFormValue>(
    initialValue ?? defaultRecurrenceValue()
  );
  const wasOpen = useRef(false);

  useEffect(() => {
    if (isOpen && !wasOpen.current) {
      setValue(initialValue ?? defaultRecurrenceValue());
    }

    wasOpen.current = isOpen;
  }, [initialValue, isOpen]);

  const maximumCount = remainingOccurrenceCount(
    sourceDate,
    maximumEndDate,
    value.recurrenceType,
    value.repeatInterval
  );
  const minimumCount = allowSingleOccurrence ? 1 : 2;
  const availableCadenceValues = getAvailableRecurrenceCadenceValues({
    sourceDate,
    maximumEndDate,
  });
  const availableCadenceOptions = cadenceOptions
    .filter((option) => availableCadenceValues.includes(option.value))
    .map((option) => ({ ...option, label: i18n._(option.label) }));
  const selectedEndDate = recurrenceEndDate(sourceDate, value, initialValue);
  const firstFutureOccurrenceDate = format(
    nextOccurrenceDateAfter(
      sourceDate,
      sourceDate,
      value.recurrenceType,
      value.repeatInterval
    ),
    "yyyy-MM-dd"
  );
  const occurrenceOptions = Array.from(
    { length: Math.max(maximumCount - minimumCount + 1, 0) },
    (_, index) => {
      const count = index + minimumCount;
      return {
        label: count === 1 ? t`1 instance` : t`${count} instances`,
        value: String(count),
      };
    }
  );
  const previewAndSave = () => {
    onSavePending({
      ...value,
      endDate:
        value.endCondition === "ON_DATE" ? selectedEndDate : value.endDate,
      occurrenceCount:
        value.endCondition === "AFTER_OCCURRENCES"
          ? Math.max(
              minimumCount,
              Math.min(value.occurrenceCount ?? 4, maximumCount)
            )
          : value.occurrenceCount,
    });
    onClose();
  };

  const cadenceValue = cadenceValueForRecurrence(value);

  return (
    <>
      <StandardModal
        isOpen={isOpen}
        onClose={onClose}
        upperTitle={initialValue ? t`Edit Recurrence` : t`Make Recurring`}
        content={
          <VStack padding={5} gap={4}>
            <VStack gap={2}>
              <Text variant="mainSmallSemiBold">{t`Repeats`}</Text>
              <DropdownSelect
                value={cadenceValue}
                options={availableCadenceOptions}
                isDisabled={lockCadence}
                onChange={(cadence) => {
                  const next = getRecurrenceForCadence(cadence);
                  const nextMaximumCount = remainingOccurrenceCount(
                    sourceDate,
                    maximumEndDate,
                    next.recurrenceType,
                    next.repeatInterval
                  );
                  const endDate = recurrenceEndDate(sourceDate, {
                    ...value,
                    ...next,
                  });

                  setValue({
                    ...value,
                    ...next,
                    endDate,
                    occurrenceCount: Math.max(
                      minimumCount,
                      Math.min(value.occurrenceCount ?? 4, nextMaximumCount)
                    ),
                  });
                }}
              />
              {lockCadence ? (
                <Text variant="mainSmallMedium" color="text-secondary">
                  {t`The repeat pattern can't be changed once earlier requests in this series exist.`}
                </Text>
              ) : null}
            </VStack>

            <VStack gap={2}>
              <Text variant="mainSmallSemiBold">{t`Ends`}</Text>
              <RadioGroup
                value={value.endCondition}
                accessibilityLabel={t`Recurrence ending`}
                accessible={false}
                onChange={(endCondition) =>
                  setValue({
                    ...value,
                    endCondition:
                      endCondition === "ON_DATE"
                        ? "ON_DATE"
                        : "AFTER_OCCURRENCES",
                    endDate:
                      endCondition === "ON_DATE"
                        ? value.endDate ?? maximumEndDate
                        : value.endDate,
                  })
                }
              >
                <VStack gap={2}>
                  <Radio value="ON_DATE" label={t`On a specific date`} />
                  {value.endCondition === "ON_DATE" ? (
                    <DatePickerInput
                      selectedDate={selectedEndDate}
                      firstAvailableDate={firstFutureOccurrenceDate}
                      lastAvailableDate={maximumEndDate}
                      clubWeekStartsOn={clubWeekStartsOn}
                      returnValueFormat="yyyy-MM-dd"
                      displayValueFormat="MMM do, yyyy"
                      accessibilityLabel={t`Recurring request end date`}
                      testID="request-recurrence-end-date"
                      onChange={(endDate) => setValue({ ...value, endDate })}
                    />
                  ) : null}
                </VStack>
                <VStack gap={2}>
                  <Radio value="AFTER_OCCURRENCES" label={t`After`} />
                  {value.endCondition === "AFTER_OCCURRENCES" ? (
                    <DropdownSelect
                      value={String(
                        Math.max(
                          minimumCount,
                          Math.min(value.occurrenceCount ?? 4, maximumCount)
                        )
                      )}
                      options={occurrenceOptions}
                      testID="request-recurrence-occurrence-count-select"
                      onChange={(count) =>
                        setValue({ ...value, occurrenceCount: Number(count) })
                      }
                    />
                  ) : null}
                </VStack>
              </RadioGroup>
            </VStack>

            <Text variant="mainSmallMedium" color="text-secondary">
              {value.endCondition === "ON_DATE"
                ? t`The original request and eligible cadence dates through ${format(
                    parseISO(value.endDate ?? maximumEndDate),
                    "MMM d, yyyy"
                  )} will be submitted.`
                : t`The original request counts as instance 1.`}
            </Text>

            <HStack gap={2}>
              <Button
                variant="secondary"
                accessibilityLabel={t`Cancel recurrence changes`}
                onPress={onClose}
              >
                {t`Cancel`}
              </Button>
              <Button
                isDisabled={maximumCount < minimumCount}
                accessibilityLabel={t`Continue to request series preview`}
                onPress={previewAndSave}
              >
                {t`Continue`}
              </Button>
            </HStack>
          </VStack>
        }
      />
    </>
  );
};
